/**
 * The day's steps as an ordered list (pedagogy spec §6.3.4, Home S1): title with the count,
 * what the step holds, its badges, "≈ N min", and a status in words (never colour alone). The
 * step the student is on carries `aria-current="step"`. Home's card and the runner both draw it.
 */
import { CheckCircle2, Circle, MinusCircle, PlayCircle } from "lucide-react";
import clsx from "clsx";
import type { ReactNode } from "react";
import { Badge } from "@client/components/ui";
import { useLang, useT, useTp } from "@client/i18n";
import { formatList, formatNumber } from "@client/lib/format";
import type { StepStatus } from "./session";
import type { TodayStep } from "./steps";

const STATUS_KEY: Record<StepStatus, string> = {
  done: "practice.today.status.done",
  skipped: "practice.today.status.skipped",
  started: "practice.today.status.current",
  pending: "practice.today.status.pending",
};

function StatusIcon({ status }: { status: StepStatus }) {
  const className = "h-5 w-5 shrink-0";
  switch (status) {
    case "done":
      return (
        <CheckCircle2
          className={clsx(className, "text-green-600 dark:text-green-400")}
          aria-hidden="true"
        />
      );
    case "skipped":
      return (
        <MinusCircle
          className={clsx(className, "text-ink-400 dark:text-ink-500")}
          aria-hidden="true"
        />
      );
    case "started":
      return (
        <PlayCircle
          className={clsx(className, "text-accent-700 dark:text-accent-400")}
          aria-hidden="true"
        />
      );
    default:
      return (
        <Circle className={clsx(className, "text-ink-400 dark:text-ink-500")} aria-hidden="true" />
      );
  }
}

export interface StepListProps {
  steps: readonly TodayStep[];
  /** A topic's name in the active language, for the new topic's title. */
  topicLabel: (topicId: string) => string;
  /** An extra control for a step (the runner's "Gör ändå" on a skipped one). */
  action?: (step: TodayStep) => ReactNode;
  className?: string;
}

export function StepList({ steps, topicLabel, action, className }: StepListProps) {
  const t = useT();
  const tp = useTp();
  const { lang } = useLang();

  function title(step: TodayStep): string {
    const count = formatNumber(step.count, lang);
    if (step.id === "cards") return t("practice.today.step.cards.title");
    if (step.id === "retest") {
      return tp(
        step.allMisses ? "practice.today.step.retest.title" : "practice.today.step.retest.titleAll",
        step.count,
        { count },
      );
    }
    const topic = step.topicId ? topicLabel(step.topicId) : "";
    // P1-3: the lecture calendar's pick says which lecture it serves ("Förbered F03: …").
    if (step.lecture) {
      return t(
        step.lecture.role === "prepare"
          ? "practice.today.step.new.prepare"
          : "practice.today.step.new.catchUp",
        { id: step.lecture.id, topic },
      );
    }
    return t("practice.today.step.new.title", { topic });
  }

  function body(step: TodayStep): string[] {
    const count = formatNumber(step.count, lang);
    if (step.id === "cards") {
      const cards = tp("practice.today.step.cards.count", step.count, { count });
      return step.chapters.length > 0
        ? [
            tp("practice.today.step.cards.body", step.count, {
              cards,
              chapters: formatList(
                step.chapters.map((chapter) => String(chapter)),
                lang,
              ),
            }),
          ]
        : [cards];
    }
    if (step.id === "retest") {
      return [
        t(
          step.allMisses ? "practice.today.step.retest.body" : "practice.today.step.retest.bodyAll",
        ),
      ];
    }
    return [
      step.worked
        ? t("practice.today.step.new.worked")
        : tp("practice.today.step.new.practice", step.count, { count }),
      ...(step.startedToday ? [t("practice.today.step.new.startedToday")] : []),
    ];
  }

  function badges(step: TodayStep): ReactNode[] {
    if (step.id === "cards") {
      return [
        <Badge key="retrieval" tone="accent">
          {t("practice.today.badge.retrieval")}
        </Badge>,
        ...(step.chapters.length > 1
          ? [
              <Badge key="mixed" tone="neutral">
                {t("practice.today.badge.mixed")}
              </Badge>,
            ]
          : []),
      ];
    }
    if (step.id === "retest") {
      return [
        <Badge key="retrieval" tone="accent">
          {t("practice.today.badge.retrieval")}
        </Badge>,
        ...(step.spacedDays > 0
          ? [
              <Badge key="spaced" tone="neutral">
                {tp("practice.today.badge.spaced", step.spacedDays, {
                  count: formatNumber(step.spacedDays, lang),
                })}
              </Badge>,
            ]
          : []),
      ];
    }
    return [
      <Badge key="new" tone="neutral">
        {t(step.worked ? "practice.today.badge.worked" : "practice.today.badge.firstProblems")}
      </Badge>,
    ];
  }

  return (
    <ol aria-label={t("practice.today.stepsLabel")} className={clsx("space-y-3", className)}>
      {steps.map((step) => (
        <li
          key={step.id}
          aria-current={step.current ? "step" : undefined}
          className={clsx(
            "flex gap-3 rounded-lg border p-3",
            step.current
              ? "border-accent-300 bg-accent-50 dark:border-accent-700 dark:bg-accent-950"
              : "border-ink-200 dark:border-ink-800",
            (step.status === "done" || step.status === "skipped") && "opacity-80",
          )}
        >
          <StatusIcon status={step.status} />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
              <p className="break-words font-medium text-ink-900 dark:text-ink-50">{title(step)}</p>
              <p className="text-sm text-ink-600 dark:text-ink-400">
                <span className="font-medium">{t(STATUS_KEY[step.status])}</span>
                {step.minutes !== null && (
                  <>
                    <span aria-hidden="true"> · </span>
                    <span>
                      {t("practice.today.minutes", { minutes: formatNumber(step.minutes, lang) })}
                    </span>
                  </>
                )}
              </p>
            </div>
            {body(step).map((line) => (
              <p key={line} className="mt-1 text-sm text-ink-600 dark:text-ink-400">
                {line}
              </p>
            ))}
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {badges(step)}
              {action?.(step)}
            </div>
          </div>
        </li>
      ))}
    </ol>
  );
}
